import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import EmailIllustration from 'public/images/giveaway-picker/GP-Share-email-illustration.png';

import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import { post } from '@/utils/client';

// Endpoints
import { GIVEAWAY_PICKER_DRAW, GIVEAWAY_PICKER_RESULT } from '@/lib/routes/apiToolsEndpoints';

import Button from '@/components/base/Button';
import Checkbox from '@/components/base/Checkbox';
import H2 from '@/components/base/Headings/H2';
import InfoBanner from '@/components/base/InfoBanner/CompoundBanner';
import Input from '@/components/base/Input';
import Label from '@/components/base/Label';
// Components
import Modal from '@/components/base/Modal';
// Atoms
import { emailAtom, optionsAtom } from '@/components/pages/giveaway-picker/Atoms/OptionsAtoms';
import { selectedPostAtom } from '@/components/pages/giveaway-picker/Atoms/postsAtoms';
import {
    giveawayCheckResultsUrlAtom,
    giveawayDrawIdAtom
} from '@/components/pages/giveaway-picker/Atoms/resultsAtom';
import { socialProfileAtom } from '@/components/pages/giveaway-picker/Atoms/socialProfileAtom';
import { nextStepAtom } from '@/components/pages/giveaway-picker/Atoms/stepsAtoms';
// Types
import {
    DrawPayload,
    ShareEmailModalProps
} from '@/components/pages/giveaway-picker/Types/drawTypes';

type GiveawayPickerDataType = {
    data: {
        check_signature: string;
        result_id: string;
    };
};

const ModalBody = ({ toggle }: Pick<ShareEmailModalProps, 'toggle'>) => {
    const { t } = useTranslation('giveaway_picker');

    const emailRef = useRef<HTMLInputElement>(null);
    const checkboxRef = useRef<HTMLInputElement>(null);

    const options = useAtomValue(optionsAtom);
    const selectedPost = useAtomValue(selectedPostAtom);
    const socialProfile = useAtomValue(socialProfileAtom);
    const [checkResultUrl, setCheckResultUrl] = useAtom(giveawayCheckResultsUrlAtom);
    const setDrawId = useSetAtom(giveawayDrawIdAtom);
    const goToNextStep = useSetAtom(nextStepAtom);
    const setEmail = useSetAtom(emailAtom);

    const [isLoading, setIsLoading] = useState(false);

    const [formMessages, setFormMessages] = useState<{
        successMessage: boolean;
        errorMessages: string[];
    }>({ successMessage: false, errorMessages: [] });

    useEffect(() => {
        if (checkResultUrl) {
            setIsLoading(false);
            toggle();
            goToNextStep();
        }
    }, [checkResultUrl]);

    const handleConfirm = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const email = emailRef.current?.value;
        const toolsMarketingConsent = checkboxRef.current?.checked;
        if (!email || email.trim() === '') {
            setFormMessages({
                successMessage: false,
                errorMessages: [t('share_email.email_error')]
            });
            return;
        }

        setIsLoading(true);
        setEmail(email);
        try {
            const payload: DrawPayload = {
                mediaId: selectedPost.id,
                socialNetworkId: socialProfile.id,
                slug: socialProfile.slug,
                email: email,
                tools_marketing_consent: toolsMarketingConsent,
                ...options
            };
            const response = await post<GiveawayPickerDataType>(GIVEAWAY_PICKER_DRAW, payload);
            const checkSignature = response.data.check_signature;
            const checkId = response.data.result_id;
            setDrawId(checkId);
            setCheckResultUrl(
                `${GIVEAWAY_PICKER_RESULT}/${checkId}/check?signature=${checkSignature}`
            );
            setFormMessages({ successMessage: true, errorMessages: [] });
        } catch (error) {
            if (error.response && error.response.status === 422) {
                const errorsKeys = Object.keys(error.response.data.errors);
                const messages = errorsKeys.map((errorKey) => {
                    if (errorKey === 'email') {
                        return t('share_email.email_error');
                    }
                    return `${String(errorKey)}.${error.response.data.errors[errorKey][0]}`;
                });
                setFormMessages({ successMessage: false, errorMessages: messages });
                return;
            }

            setFormMessages({
                successMessage: false,
                errorMessages: [t('share_email.generic_error')]
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal.Body className="flex">
            <form className="flex w-full" onSubmit={handleConfirm} data-testid="share-email-form">
                <div className="flex w-2/5 flex-col justify-center space-y-4 rounded-l-lg bg-icoGray-100 text-center">
                    <Image src={EmailIllustration} alt="Email Illustration" className="mb-10" />
                </div>
                <div className="flex w-3/5 flex-col gap-10 p-5 sm:p-10">
                    <span>
                        <H2 className="mb-6 font-GBold text-4xl">{t('share_email.title')}</H2>
                        <p className="mb-10 font-GMedium text-icoGray-500">
                            {t('share_email.description')}
                        </p>
                        <div className="mb-20">
                            {formMessages.errorMessages.length > 0 ? (
                                <div className="mb-4">
                                    <InfoBanner>
                                        <InfoBanner.Icon />
                                        <InfoBanner.Content
                                            title={t('errors:error_occured')}
                                            message={formMessages.errorMessages.join(', ')}
                                        />
                                    </InfoBanner>
                                </div>
                            ) : null}
                            <Label
                                htmlFor="email"
                                className="mb-2 block font-GMedium text-sm text-icoGray-500">
                                {t('share_email.email_label')}
                            </Label>
                            <Input
                                type="email"
                                id="email"
                                ref={emailRef}
                                placeholder={t('share_email.email_placeholder')}
                                data-testid="email-input"
                            />
                            <Label fontType="legalCheckbox" className="relative mt-5 pl-5">
                                <Checkbox
                                    id="newsletter"
                                    boxSize="sm"
                                    className="absolute left-0 top-0.5"
                                    ref={checkboxRef}
                                />
                                {t('share_email.checkbox_newsletter')}
                            </Label>
                        </div>
                    </span>
                    <div className="flex flex-col-reverse justify-between gap-5 sm:flex-row sm:gap-0">
                        <button
                            type="button"
                            onClick={toggle}
                            className="text-sm text-icoGray-500 underline"
                            data-testid="share-email-modal-cancel">
                            {t('commons:cancel')}
                        </button>
                        <Button
                            type="submit"
                            data-testid="share-email-modal-confirm"
                            isLoading={isLoading}>
                            {t('commons:confirm')}
                        </Button>
                    </div>
                </div>
            </form>
        </Modal.Body>
    );
};

export const ShareEmailModal = ({ isOpen, toggle }: ShareEmailModalProps) => {
    return (
        <Modal open={isOpen} onDismiss={toggle} size="4xl" data-testid="share-email-modal">
            <ModalBody toggle={toggle} />
        </Modal>
    );
};
